import { useQuery } from 'react-query';
import { useMemo } from 'react';
import { getPCForWarehouseList } from 'src/utils/Queries';
import { GetPCForWarehouseListResponse } from 'src/features/types/gslaApi';

/**
 * React Query hook for fetching production capabilities for multiple warehouses
 * with automatic Default PC appending and caching
 */
export const usePCForWarehouse = (
    warehouseIds: string[],
    enabled: boolean = true
) => {
    // Sort warehouse IDs for consistent cache keys
    const sortedWarehouseIds = useMemo(() =>
        [...warehouseIds].sort(),
        [warehouseIds]
    );

    // React Query hook with 10-minute cache duration
    const queryResult = useQuery<GetPCForWarehouseListResponse, Error>(
        ['pc-for-warehouse', ...sortedWarehouseIds],
        async () => {
            const result = getPCForWarehouseList(sortedWarehouseIds);
            if (!result) {
                throw new Error('Failed to fetch production capabilities');
            }
            return result;
        },
        {
            enabled: enabled && sortedWarehouseIds.length > 0,
            staleTime: 10 * 60 * 1000, // 10 minutes
            cacheTime: 15 * 60 * 1000, // 15 minutes
            retry: 2,
            retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
            // Don't refetch on window focus to avoid unnecessary API calls
            refetchOnWindowFocus: false,
        }
    );

    // Process the API response and append default PCs
    const availablePCs = useMemo(() => {
        if (!queryResult.data) return [];

        const allPCs = new Set<string>();
        
        // Add PCs from API response
        Object.values(queryResult.data.productionCapabilitiesByFC).forEach((pcs: string[]) => {
            pcs.forEach((pc: string) => allPCs.add(pc));
        });
        
        // Always append default PCs as per requirements
        allPCs.add('Default_FC_PC');
        allPCs.add('Default_Network_PC');
        
        return Array.from(allPCs).sort();
    }, [queryResult.data]);

    return {
        ...queryResult,
        availablePCs,
        isLoading: queryResult.isLoading,
        error: queryResult.error,
        isError: queryResult.isError,
        isSuccess: queryResult.isSuccess,
        refetch: queryResult.refetch,
    };
};

/**
 * Simplified hook that returns just the production capabilities array
 * for easier integration with existing components
 */
export const useProductionCapabilities = (
    warehouseIds: string[],
    enabled: boolean = true
): string[] => {
    const { availablePCs } = usePCForWarehouse(warehouseIds, enabled);
    return availablePCs;
};